import type { ReactNode } from 'react';
import type { Observable } from 'rxjs';
import type {
  CareExecutionReport,
  CustomerExecutionReport,
  CustomerQuote,
  CustomerTrade,
  CustomerTransaction,
  ExecutionReport,
  Quote,
} from '../../types';
import type { TimelineItemProps } from '../Timeline';

export interface ExecutionTimelineProps {
  /** Automatically groups similar items (such as trades) in a TimelineGroup. */
  groupSimilarItems?: boolean;

  /** Shows who's the initiator of the event (e.g. "Customer", "Dealer", "Lender", "Borrower", etc.) */
  showInitiatorLabels?: boolean;

  /** Show items in reversed order, newest first */
  reverse?: boolean;

  /** Statuses to filter out entries */
  statuses?: string[];

  /** Optional callback to render additional action buttons for each timeline item based on its raw data. */
  getItemActions?: (rawData: WithTimestamp) => ReactNode;

  /** Renders every timeline row in normal document flow instead of a virtualized scroll container. */
  naturalHeight?: boolean;

  customerExecutionReports?: Observable<CustomerExecutionReport[]>;
  customerQuotes?: Observable<CustomerQuote[]>;
  executionReports?: Observable<ExecutionReport[]>;
  quotes?: Observable<Quote[]>;
  customerTrades?: Observable<CustomerTrade[]>;
  customerTransactions?: Observable<CustomerTransaction[]>;
  careExecutionReports?: Observable<CareExecutionReport[]>;
}

export type ExecutionTimelineEntry<RawData> =
  | ExecutionTimelineItemEntry<RawData>
  | ExecutionTimelineGroupEntry<RawData>;

export interface ExecutionTimelineItemEntry<RawData> {
  props: TimelineItemProps;
  rawData: RawData;
  type: 'item';
}

export interface ExecutionTimelineGroupEntry<RawData> {
  items: ExecutionTimelineItemEntry<RawData>[];
  type: 'group';
}

export interface WithTimestamp {
  Timestamp: string;
}

export const TimelineStatusEnum = {
  Canceled: 'Canceled',
  Rejected: 'Rejected',
  PendingNew: 'Submitted',
  PendingPause: 'Pause requested',
  Paused: 'Paused',
  PendingResume: 'Resume requested',
  Resumed: 'Resumed',
  Replaced: 'Modified',
  ReplaceRejected: 'Modification rejected',
  Trade: 'Traded',
  Triggered: 'Triggered',
} as const;
export type TimelineStatusEnum = (typeof TimelineStatusEnum)[keyof typeof TimelineStatusEnum];
